import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export default function Hero() {
  const goDown = useCallback(() => {
    const next = document.getElementById("about");
    if (!next) return;
    const y = next.getBoundingClientRect().top + window.scrollY - 64; // header offset
    window.scrollTo({ top: y, behavior: "smooth" });
  }, []);

  // Typewriter for the subtitle line ONLY
  const phrases = useMemo(
    () => [
      "I craft digital experiences for the curious and bold.",
      "Building fast, accessible, animated web apps.",
      "Designing delightful UIs with robust engineering.",
      "Realtime apps with React + Firebase.",
    ],
    []
  );
  const typingSpeed = 42;
  const backspaceSpeed = 28;
  const holdTime = 1200;

  const [i, setI] = useState(0);
  const [line, setLine] = useState("");
  const [del, setDel] = useState(false);
  const tRef = useRef(0);

  useEffect(() => {
    const current = phrases[i % phrases.length];

    const step = () => {
      if (!del) {
        if (line.length < current.length) {
          setLine(current.slice(0, line.length + 1));
          tRef.current = window.setTimeout(step, typingSpeed);
        } else {
          tRef.current = window.setTimeout(() => {
            setDel(true);
            step();
          }, holdTime);
        }
      } else {
        if (line.length > 0) {
          setLine(current.slice(0, line.length - 1));
          tRef.current = window.setTimeout(step, backspaceSpeed);
        } else {
          setDel(false);
          setI((x) => (x + 1) % phrases.length);
          tRef.current = window.setTimeout(step, typingSpeed);
        }
      }
    };

    tRef.current = window.setTimeout(step, del ? backspaceSpeed : typingSpeed);
    return () => window.clearTimeout(tRef.current);
  }, [line, del, i, phrases]);

  return (
    <div className="relative min-h-[100svh] grid place-items-center overflow-hidden">
      {/* Background gradients */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 animate-gradient-move bg-[linear-gradient(135deg,_color-mix(in_oklab,var(--color-primary)_22%,transparent),_color-mix(in_oklab,var(--color-secondary)_22%,transparent),_color-mix(in_oklab,var(--color-accent)_22%,transparent))] opacity-40"
      />
      <div className="absolute -top-40 -left-40 h-[38rem] w-[38rem] rounded-full bg-[color:var(--color-primary)]/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 h-[38rem] w-[38rem] rounded-full bg-[color:var(--color-secondary)]/10 blur-3xl" />

      {/* Content */}
      <div className="container-page mx-auto text-center">
        <span className="block text-sm tracking-widest uppercase text-[--color-primary]/90 mb-4">
          ISHAN JAISWAL
        </span>

        <h1 className="h1 text-balance">
          Hey <span className="inline-block animate-float-slow">👋</span> I’m Ishan
        </h1>

        {/* Subtitle with typewriter effect */}
        <p className="mt-4 text-lg text-balance mx-auto max-w-2xl">
          <span className="relative whitespace-pre">
            {line}
            <span className="ml-1 inline-block h-[1.05em] w-[2px] translate-y-[2px] align-middle bg-current animate-[blink_1s_steps(1)_infinite]" />
          </span>
        </p>

        <div className="mt-8 flex items-center justify-center gap-3">
          <a
            href="#projects"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("projects");
              if (!el) return;
              const y = el.getBoundingClientRect().top + window.scrollY - 64;
              window.scrollTo({ top: y, behavior: "smooth" });
            }}
            className="btn btn-solid"
          >
            See my work
          </a>

        <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("contact");
              if (!el) return;
              const y = el.getBoundingClientRect().top + window.scrollY - 64;
              window.scrollTo({ top: y, behavior: "smooth" });
            }}
            className="btn btn-glow text-black dark:text-black"
          >
            Let’s chat
          </a>
        </div>
      </div>

      {/* Scroll-down cue: keep as-is */}
      <button
        onClick={goDown}
        aria-label="Scroll to next section"
        className="group absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <span className="relative grid h-12 w-12 place-items-center rounded-full bg-[color:var(--color-bg-dark)]/40 backdrop-blur-sm border border-white/10">
          <span className="absolute -inset-[3px] -z-10 rounded-full bg-[conic-gradient(from_0deg,rgba(0,245,160,.8),rgba(255,0,127,.8),rgba(140,48,255,.8),rgba(0,245,160,.8))] blur-md opacity-80 group-hover:opacity-100 transition-opacity" />
          <svg
            className="h-5 w-5 text-[--color-primary] animate-bounce"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M6 13l6 6 6-6" />
          </svg>
        </span>
      </button>

      {/* local keyframes */}
      <style>{`
        @keyframes blink { 0%, 49% { opacity: 1 } 50%, 100% { opacity: 0 } }
      `}</style>
    </div>
  );
}